export type ListItem = {
  id: string; title: string; alt: string; width: number; height: number; color: string; lqip: string;
  status: string; analysis: string; photographer: string; tags: string[]; pending: number;
  blocked: string | null; consent: string; version: number; vision: string; claude: string;
  issues: { key: string; label: string; level: "block" | "todo" }[];
};
export type Facets = {
  tags: { name: string; category: string; n: number }[];
  photographers: { name: string; n: number }[];
  colors: { name: string; hex: string; n: number }[];
};
export type Collection = {
  id: string; name: string; description: string; created_at: string; updated_at: string;
  count: number; covers: { id: string; version: number; color: string }[];
};

export type Asset = {
  id: string; slug: string; filename: string; ext: string; width: number; height: number; bytes: number;
  title: string; alt: string; description: string; photographer: string; license: string; license_note: string;
  license_expires: string | null; consent: string; consent_note: string; people_count: number | null;
  taken_at: string | null; camera: string | null; lat: number | null; lng: number | null; place: string;
  color: string; lqip: string; focal_x: number; focal_y: number; ocr_text: string; status: string;
  analysis: string; ai_alt: string; ai_title: string; version: number; created_at: string; updated_at: string; vision: string; claude: string;
  ai_description: string; ai_written: string; ai_text_source: string;
};
export type Suggestion = { name: string; confidence: number; source: string; reason: string };
export type Detail = {
  asset: Asset & { hasPrint?: boolean };
  collections: { id: string; name: string }[];
  tags: { name: string; category: string; source: string }[];
  suggestions: Suggestion[];
  usages: { page_url: string; first_seen: string; last_seen: string; hits: number }[];
  events: { id: number; at: string; kind: string; detail: string }[];
  similar: { id: string; title: string; color: string; lqip: string; distance: number }[];
  blocked: string | null;
  publishProblems: string[];
  links: { meta: string; image: string; original: string; oembed: string };
  metadata: unknown;
};
export type Me = { id: number; email: string; name: string; role: "admin" | "anvandare"; mustChange: boolean };
export type AgentStatus = {
  agents: { id: number; name: string; created_at: string; last_seen: string | null; analysed: number; online: boolean }[];
  waiting: number; online: boolean;
};
export type Status = {
  ai: { vision: boolean; visionMode: "local" | "agent" | "off"; claude: boolean; settings: { autoVision: boolean; autoClaude: boolean } };
  agent: AgentStatus | null;
  licenses: Record<string, string>; consents: Record<string, string>;
  categories: string[]; vocabulary: string[];
};

export class ApiError extends Error {
  constructor(message: string, public status: number, public body: any) { super(message); }
}

export async function api<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<T> {
  const res = await fetch(`/api/${path}`, {
    ...init,
    headers: init?.json !== undefined ? { "content-type": "application/json" } : undefined,
    body: init?.json !== undefined ? JSON.stringify(init.json) : init?.body,
  });
  const body = await res.json().catch(() => ({}));
  // Sessionen har gått ut eller kontot stängts – visa inloggningen igen.
  if (res.status === 401 && path !== "me" && !path.startsWith("auth/")) window.dispatchEvent(new Event("bildbanken:logged-out"));
  if (!res.ok) throw new ApiError(body.error ?? `Fel ${res.status}`, res.status, body);
  return body as T;
}

/** Bild via leveranslänken (fungerar även för utkast inifrån bildbanken). */
export const img = (a: { id: string; version?: number }, w = 640, ratio?: string, fmt = "webp") =>
  `/i/${a.id}/bild.${fmt}?w=${w}${ratio ? `&ratio=${encodeURIComponent(ratio)}` : ""}&v=${a.version ?? 1}`;

export const STATUS_LABEL: Record<string, string> = { utkast: "Utkast", publicerad: "Publicerad", arkiverad: "Arkiverad" };
export const SOURCE_LABEL: Record<string, string> = {
  vision: "Apple Vision", claude: "Claude", exif: "Filinfo", plats: "GPS", fotografering: "Samma fotografering",
};
export const sourceLabel = (s: string) => s.split("+").map((x) => SOURCE_LABEL[x] ?? x).join(" + ");

/** SQLite lagrar datetime('now') i UTC utan tidszon – visa i svensk lokal tid. */
export const localTime = (ts: string | null | undefined, withTime = true) => {
  if (!ts) return "";
  const d = new Date(ts.includes("T") ? ts : `${ts.replace(" ", "T")}Z`);
  return withTime
    ? d.toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })
    : d.toLocaleDateString("sv-SE");
};

/** Pågående AI-analys för en bild (köad eller körs). */
export const aiBusy = (a: { vision: string; claude: string }) =>
  a.vision === "pending" || a.vision === "claimed" || a.claude === "pending";
